import React, { useState, useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import styled from 'styled-components';
import { OutputRow } from "../../../../utils/budget-tool-specific";
import { RebelColors, RebelButton } from "../../../../styles/rebel-experiences";
import RebelSummaryExperienceRow from "./rebel-summary-experience-row";
import { GetDB } from "../../../../utils/database/firebase-budget-tool";
import { listenToDocumentProperty, updateRankings } from "../../../../utils/firestore-functions";
import { getVoterCookie, updateVoterCookie } from "../../../../utils/ranking-cookies";

const RebelSummaryTabForm = ({guideData, client = false, guideId}) => {

    const [selectedRankings, setSelectedRankings] = useState({});
    const [savedRankings, setSavedRankings] = useState({});
    const [allRankings, setAllRankings] = useState({});
    const [voterId, setVoterId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const budgetDB = GetDB();

    // Initialize voter ID from cookie when component mounts and we have a guideId
    useEffect(() => {
        if (client && guideId) {
            const voterData = getVoterCookie(guideId);
            setVoterId(voterData.voterId);
            setSelectedRankings(voterData.lastSubmitted || {});
            setSavedRankings(voterData.lastSubmitted || {});
        }
    }, [client, guideId]);

    // Listen to all rankings from database
    useEffect(() => {
        if (!guideId) return;

        const unsubscribe = listenToDocumentProperty(
            budgetDB,
            "rebelExperienceGuides",
            guideId,
            "rankings",
            (rankings) => {
                setAllRankings(rankings || {});

                // If this voter has submitted rankings, load them
                if (client && voterId && rankings && rankings[voterId]) {
                    const voterRankings = rankings[voterId].votes || {};
                    setSavedRankings(voterRankings);
                    // Only update selected if user hasn't made changes
                    if (Object.keys(selectedRankings).length === 0) {
                        setSelectedRankings(voterRankings);
                    }
                }
            },
            (error) => {
                console.error("Error listening to rankings:", error);
            }
        );

        return () => unsubscribe();
    }, [guideId, voterId, client]);

    // Handle ranking change
    const handleRankingChange = (experienceId, rank) => {
        setSelectedRankings(prev => {
            const newRankings = { ...prev };
            if (rank === null) {
                delete newRankings[experienceId];
            } else {
                newRankings[experienceId] = rank;
            }
            return newRankings;
        });
    };

    // Check if all experiences are ranked
    const isComplete = () => {
        const numExperiences = Object.keys(guideData.experiences.value).length;
        return Object.keys(selectedRankings).length === numExperiences &&
               Object.values(selectedRankings).every(rank => rank !== null && rank !== undefined);
    };

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
        const savedKeys = Object.keys(savedRankings).sort();
        const selectedKeys = Object.keys(selectedRankings).sort();

        if (savedKeys.length !== selectedKeys.length) return true;

        return selectedKeys.some(key =>
            savedRankings[key] !== selectedRankings[key]
        );
    };

    // Submit rankings to database
    const handleSubmitRankings = async () => {
        if (!isComplete()) {
            alert("Please rank all experiences before submitting.");
            return;
        }

        if (!voterId || !guideId) {
            alert("Unable to submit rankings. Please refresh the page and try again.");
            return;
        }

        setIsSubmitting(true);

        try {
            await updateRankings(
                budgetDB,
                guideId,
                voterId,
                selectedRankings,
                () => {
                    // Success callback
                    updateVoterCookie(guideId, voterId, selectedRankings);
                    setSavedRankings(selectedRankings);
                    alert("Rankings submitted successfully!");
                    setIsSubmitting(false);
                },
                (error) => {
                    // Error callback
                    console.error("Error submitting rankings:", error);
                    alert("Failed to submit rankings. Please try again.");
                    setIsSubmitting(false);
                }
            );
        } catch (error) {
            console.error("Error submitting rankings:", error);
            alert("Failed to submit rankings. Please try again.");
            setIsSubmitting(false);
        }
    };

    const totalExperiences = Object.keys(guideData.experiences.value).length;
    const showSubmitButton = client && totalExperiences > 0;
    const isButtonDisabled = !isComplete() || !hasUnsavedChanges() || isSubmitting;

    return (
        <React.Fragment>
            <StyledContainer>
                <RebelOutputRow style={{display: "flex", alignItems: "flex-end"}}>
                    <TallyHeaderCol lg="1" xs="1">
                        <RebelBoldLabel>
                            Tally
                        </RebelBoldLabel>
                    </TallyHeaderCol>
                    <Col>
                        <RebelBoldLabel>
                            R.E.B.E.L Experience
                        </RebelBoldLabel>
                    </Col>
                    <Col lg="2">
                        <RebelBoldLabel>
                            Cost Per Attendee
                        </RebelBoldLabel>
                    </Col>
                    <Col lg="2">
                        <RebelBoldLabel>
                            Total Est. Cost of Trip
                        </RebelBoldLabel>
                    </Col>
                    {client && <RankingHeaderCol lg="1" xs="1">
                        <RebelBoldLabel>
                            Ranking
                        </RebelBoldLabel>
                    </RankingHeaderCol>}
                    <Col lg="1">
                    </Col>
                </RebelOutputRow>
                {Object.values(guideData.experiences.value).sort((a,b) => {
                    return parseInt(a.index) - parseInt(b.index);
                }).map((experience, i) => {
                    let expData = {   title: {value: experience.title, invalid: false, required: true},
                                    destCurrency: {value: experience.destCurrency, invalid: false, required: true},
                                    base: {value: experience.base, invalid: false, required: true},
                                    convert: {value: experience.convert, invalid: false, required: true},
                                    location: {value: experience.location, invalid: false, required: true},
                                    flightCost: {value: experience.flightCost, invalid: false, required: true},
                                    costPerTransportation: {value: experience.costPerTransportation, invalid: false, required: true},
                                    transportation: {value: experience.transportation || {}, invalid: false, required: true},
                                    costPerActivity: {value: experience.costPerActivity, invalid: false, required: true},
                                    numActivities: {value: experience.numActivities, invalid: false, required: true},
                                    costPerBreakfast: {value: experience.costPerBreakfast, invalid: false, required: true},
                                    numBreakfast: {value: experience.numBreakfast, invalid: false, required: true},
                                    costPerLunch: {value: experience.costPerLunch, invalid: false, required: true},
                                    numLunch: {value: experience.numLunch, invalid: false, required: true},
                                    costPerDinner: {value: experience.costPerDinner, invalid: false, required: true},
                                    numDinner: {value: experience.numDinner, invalid: false, required: true},
                                    costPerAlcohol: {value: experience.costPerAlcohol, invalid: false, required: true},
                                    numAlcohol: {value: experience.numAlcohol, invalid: false, required: true},
                                    roomRental: {value: experience.roomRental, invalid: false, required: true},
                                    av: {value: experience.av, invalid: false, required: true},
                                    setupFee: {value: experience.setupFee, invalid: false, required: true},
                                    costPerCoffee: {value: experience.costPerCoffee, invalid: false, required: true},
                                    numCoffee: {value: experience.numCoffee, invalid: false, required: true},
                                    numMeetings: {value: experience.numMeetings, invalid: false, required: true},
                                    miscellaneousCosts: {value: experience.miscellaneousCosts, invalid: false, required: true},
                                    index: experience.index ? experience.index : 0,
                                    villaRentalFee: {value: experience.villaRentalFee ? experience.villaRentalFee : 0, invalid: false, required: true},
                                    cleaningFee: {value: experience.cleaningFee ? experience.cleaningFee : 0, invalid: false, required: true},
                                    villaId: {value: experience.villaId ? experience.villaId : "", invalid: false, required: true},
                                    villaName: {value: experience.villaName ? experience.villaName : "", invalid: false, required: true},
                    }
                    return (
                        <RebelSummaryExperienceRow
                            key={i}
                            guideData={guideData}
                            expData={expData}
                            experienceId={experience.id}
                            client={client}
                            allRankings={allRankings}
                            selectedRankings={selectedRankings}
                            onRankingChange={handleRankingChange}
                            totalExperiences={totalExperiences}
                        />
                    )
                })}
                {showSubmitButton && (
                    <SubmitButtonContainer>
                        <SubmitButton
                            onClick={handleSubmitRankings}
                            disabled={isButtonDisabled}
                        >
                            {isSubmitting ? 'Submitting...' :
                             Object.keys(savedRankings).length > 0 ? 'Update Rankings' : 'Submit Rankings'}
                        </SubmitButton>
                    </SubmitButtonContainer>
                )}
            </StyledContainer>
        </React.Fragment>
    )
}

export default RebelSummaryTabForm;

const StyledContainer = styled.div`
    margin-left: 1px;
    margin-right: 1px;
    padding: 15px;
    border-radius: 0px 0px 10px 10px;
    border: 2px solid ${RebelColors.gold};
    background-color: ${RebelColors.surface};
`;

const RebelOutputRow = styled(OutputRow)`
    border-bottom: 1px solid ${RebelColors.border};
`;

const RebelBoldLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 600;
    margin-bottom: 8px;
`

const SubmitButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 2px solid ${RebelColors.border};
`;

const SubmitButton = styled(RebelButton)`
    width: 100%;
    max-width: 300px;
    padding: 12px 24px;
    font-size: 1rem;
    font-weight: 600;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background-color: ${RebelColors.border};
        border-color: ${RebelColors.border};
    }
`;

const TallyHeaderCol = styled(Col)`
    text-align: center;
    padding-left: 5px !important;
    padding-right: 5px !important;
`;

const RankingHeaderCol = styled(Col)`
    text-align: center;
`;

